import loader from "@monaco-editor/loader";
import type * as Monaco from "monaco-editor";
import xmlFormat from "xml-formatter";
import { getXmlContext, getCompletions, validateXml, errorsToMarkers } from "./xml-language-service";

// Configure Monaco to load from CDN
loader.config({
  paths: {
    vs: "https://cdn.jsdelivr.net/npm/monaco-editor@0.52.2/min/vs",
  },
});

/**
 * Format XML string with proper indentation
 */
export function formatXml(xml: string): string {
  return xmlFormat(xml, {
    indentation: "  ",
    collapseContent: true,
    lineSeparator: "\n",
  });
}

// Monaco instance (loaded asynchronously)
let monacoInstance: typeof Monaco | null = null;
let loadPromise: Promise<typeof Monaco> | null = null;
let completionProviderRegistered = false;

/**
 * Register XML completion provider
 */
function registerXmlCompletionProvider(monaco: typeof Monaco) {
  if (completionProviderRegistered) return;
  completionProviderRegistered = true;

  monaco.languages.registerCompletionItemProvider("xml", {
    triggerCharacters: ["<", " ", "/", '"', "'"],
    provideCompletionItems: (model, position) => {
      const content = model.getValue();
      const offset = model.getOffsetAt(position);
      const isXsl = content.includes("xsl:stylesheet") || content.includes("xmlns:xsl");

      // Get the word range for replacement
      const wordInfo = model.getWordUntilPosition(position);
      const range: Monaco.IRange = {
        startLineNumber: position.lineNumber,
        startColumn: wordInfo.startColumn,
        endLineNumber: position.lineNumber,
        endColumn: wordInfo.endColumn,
      };

      // Check what character triggered completion
      const lineContent = model.getLineContent(position.lineNumber);
      const charBefore = lineContent[position.column - 2];
      const charAfter = lineContent[position.column - 1];

      // Adjust range if triggered by < or /
      // Also include any auto-inserted > after cursor
      let adjustedRange = range;
      if (charBefore === "<") {
        // Check if Monaco auto-inserted a > after the cursor
        const endColumn = charAfter === ">" ? position.column + 1 : position.column;
        adjustedRange = {
          ...range,
          startColumn: position.column,
          endColumn,
        };
      } else if (charAfter === ">") {
        // Include the trailing > in replacement range
        adjustedRange = {
          ...range,
          endColumn: range.endColumn + 1,
        };
      }

      const context = getXmlContext(content, offset);
      const suggestions = getCompletions(context, isXsl, monaco, adjustedRange);

      return { suggestions };
    },
  });
}

/**
 * Validate XML and set markers on the editor model
 */
export function validateAndSetMarkers(
  monaco: typeof Monaco,
  model: Monaco.editor.ITextModel,
  ownerId: string
) {
  const content = model.getValue();
  const errors = validateXml(content);
  const markers = errorsToMarkers(errors, monaco);
  monaco.editor.setModelMarkers(model, ownerId, markers);
}

export const getMonaco = async (): Promise<typeof Monaco> => {
  if (monacoInstance) return monacoInstance;

  if (!loadPromise) {
    loadPromise = loader.init().then((monaco) => {
      monacoInstance = monaco;

      // Define light theme
      monaco.editor.defineTheme("xsltransform-light", {
        base: "vs",
        inherit: true,
        rules: [],
        colors: {
          "editor.background": "#ffffff",
          "editor.lineHighlightBackground": "#f5f5f5",
        },
      });

      // Define dark theme
      monaco.editor.defineTheme("xsltransform-dark", {
        base: "vs-dark",
        inherit: true,
        rules: [],
        colors: {
          "editor.background": "#2d3748",
          "editor.lineHighlightBackground": "#3a4556",
        },
      });

      // Register XML completion provider
      registerXmlCompletionProvider(monaco);

      return monaco;
    });
  }

  return loadPromise;
};

export const setMonacoTheme = async (dark: boolean): Promise<void> => {
  const monaco = await getMonaco();
  monaco.editor.setTheme(dark ? "xsltransform-dark" : "xsltransform-light");
};
